import { useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '@/components/base/Modal';
import ReceiptModal from '@/components/feature/ReceiptModal';
import { downloadShippingLabel } from '@/lib/shippingLabel';
import { METHOD_LABELS } from '@/services/payments';
import { reopenSistecreditoFinancing } from '@/services/sistecredito';
import { useBrandIdentity } from '@/hooks/useBrandIdentity';
import type { Order } from '@/services/orders';

interface OrderDetailModalProps {
  order: Order;
  onClose: () => void;
  // Abre el modal "Confirmar Venta" precargado con este pedido para reintentar
  // el cobro con otro método de pago (tras un rechazo de Sistecrédito).
  onRetryWithMethod?: (order: Order) => void;
}

const STATUS_LABELS: Record<string, string> = {
  received: 'Recibido',
  pending_payment: 'Pendiente de Pago',
  payment_initiated: 'Pago Iniciado',
  payment_confirmed: 'Pago Confirmado',
  listo_envio: 'Pendiente de envío',
  preparing: 'Preparando',
  packed: 'Empacado',
  shipped: 'Despachado',
  in_transit: 'En Tránsito',
  delivered: 'Entregado',
  closed: 'Cerrado',
  cancelled: 'Cancelado',
};

// Ventana flotante con el detalle completo de un pedido: cliente, productos,
// pago, datos de despacho y accesos a comprobante y guía de envío. Se abre
// desde el listado de Pedidos con el mismo botón de "ver detalle" que Ventas.
export default function OrderDetailModal({
  order,
  onClose,
  onRetryWithMethod,
}: OrderDetailModalProps) {
  const identity = useBrandIdentity();
  const [showReceipt, setShowReceipt] = useState(false);
  const [autoPrintReceipt, setAutoPrintReceipt] = useState(false);

  // Regenerar el enlace de financiamiento de un pedido pendiente.
  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenError, setReopenError] = useState('');
  const [reopenUrl, setReopenUrl] = useState('');
  const [reopenCopied, setReopenCopied] = useState(false);

  const total = order.total;
  const itemsTotal = order.items.reduce((sum, it) => sum + it.quantity * it.unitPrice, 0);
  const shippingCost = order.shippingCost;

  // Pedido financiado con Sistecrédito aún sin aprobación del aliado.
  const financing =
    order.paymentMethod === 'sistecredito' &&
    !order.financingRejected &&
    order.paymentStatus !== 'approved' &&
    order.status !== 'cancelled' &&
    order.status !== 'closed';

  // Resumen del último intento de Sistecrédito: estado (insignia) para el admin.
  const scrStatus = order.sistecreditoAttemptStatus;
  // El intento quedó rechazado/cancelado (aunque luego se haya reintentado con
  // otro método, la huella del rechazo se conserva para trazabilidad).
  const scrRejected =
    order.financingRejected || scrStatus === 'rejected' || scrStatus === 'cancelled';
  const scrAttemptBadge = order.financingRejected || scrStatus === 'rejected'
    ? { label: 'Rechazado', className: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' }
    : scrStatus === 'cancelled'
      ? { label: 'Cancelado / Error', className: 'bg-red-100 text-red-700', icon: 'ri-error-warning-line' }
      : scrStatus === 'approved' || order.paymentStatus === 'approved'
        ? { label: 'Aprobado', className: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-line' }
        : { label: 'Pendiente', className: 'bg-amber-100 text-amber-700', icon: 'ri-time-line' };

  // Insignia por estado de un intento individual del historial de Sistecrédito.
  function attemptStatusBadge(status: string) {
    if (status === 'approved') {
      return { label: 'Aprobado', className: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-line' };
    }
    if (status === 'rejected') {
      return { label: 'Rechazado', className: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' };
    }
    if (status === 'cancelled') {
      return { label: 'Cancelado / Error', className: 'bg-red-100 text-red-700', icon: 'ri-error-warning-line' };
    }
    return { label: 'Pendiente', className: 'bg-amber-100 text-amber-700', icon: 'ri-time-line' };
  }

  async function handleReopen() {
    setReopenLoading(true);
    setReopenError('');
    setReopenUrl('');
    setReopenCopied(false);
    try {
      const result = await reopenSistecreditoFinancing({
        orderNumber: order.orderNumber,
        orderId: order.id,
      });
      if (result.redirectUrl) {
        setReopenUrl(result.redirectUrl);
      } else {
        setReopenError(
          'Sistecrédito no devolvió un enlace. Verifica que esté activado y con credenciales en el panel.',
        );
      }
    } catch (err) {
      setReopenError((err as Error)?.message ?? 'No se pudo regenerar el enlace.');
    } finally {
      setReopenLoading(false);
    }
  }

  async function handleCopyLink() {
    if (!reopenUrl) return;
    try {
      await navigator.clipboard.writeText(reopenUrl);
      setReopenCopied(true);
    } catch {
      setReopenCopied(false);
    }
  }

  function handleShippingLabel(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    downloadShippingLabel({
      companyName: identity.companyName,
      companyPhone: identity.companyPhone,
      companyCity: identity.companyCity,
      companyLogo: identity.logo,
      senderName: identity.senderName,
      senderId: identity.senderId,
      senderPhone: identity.senderPhone,
      senderAddress: identity.senderAddress,
      senderCity: identity.senderCity,
      recipientName: order.recipientName || order.customerName,
      recipientPhone: order.recipientPhone || order.customerPhone,
      recipientId: order.recipientIdNumber,
      recipientEmail: order.recipientEmail || order.customerEmail,
      recipientCity: order.shippingCity || order.customerCity,
      recipientDepartment: order.shippingDepartment,
      recipientAddress: order.shippingAddress,
      recipientNotes: order.notes,
      orderNumber: order.orderNumber,
      orderDate: order.createdAt,
      contents: order.items
        .map((it) => `${it.productName} · ${it.sizeLabel} × ${it.quantity}`)
        .join(', '),
      paymentMode:
        order.paymentMethod === 'cash' ? 'Cobro Contraentrega' : 'Flete Pagado',
      total: order.paymentMethod === 'cash' ? order.total : 0,
    });
  }

  return (
    <>
      <Modal
        title="Detalle de Pedido"
        subtitle={order.orderNumber}
        onClose={onClose}
        maxWidth="max-w-2xl"
      >
        <div className="space-y-4">
          {order.hasShipping && (
            <div className="flex items-center gap-2 text-sm bg-secondary-100 text-secondary-900 border border-secondary-200 rounded-lg px-3 py-2.5">
              <i className="ri-truck-line"></i>
              <span>Este pedido <strong>requiere envío</strong> con Interrapidísimo.</span>
            </div>
          )}

          {/* Aviso destacado: el financiamiento fue rechazado. Se recomienda
              contactar al cliente y reintentar con otro método de pago. */}
          {order.financingRejected && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-3">
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-circle-line text-red-600 text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-800">
                    Financiamiento rechazado por Sistecrédito
                  </p>
                  <p className="text-xs text-red-700 mt-0.5 leading-relaxed">
                    El pedido <strong>{order.orderNumber}</strong> quedó cancelado y el stock se liberó;{' '}
                    <strong>no se realizó ningún cobro</strong>.
                    {order.sistecreditoRejectionReason && (
                      <> Motivo reportado: <strong>{order.sistecreditoRejectionReason}</strong>.</>
                    )}{' '}
                    Contacta al cliente y ofrécele <strong>reintentar con otro método de pago</strong>{' '}
                    (contra entrega, transferencia / Nequi o link de pago).
                  </p>
                  {onRetryWithMethod && (
                    <button
                      onClick={() => onRetryWithMethod(order)}
                      className="mt-2.5 inline-flex items-center gap-1.5 text-xs px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap font-medium cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i>Reintentar con otro método
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cabecera: estado + canal */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Estado</p>
              <p className="text-sm font-medium text-foreground-900">
                {financing ? 'En financiamiento' : STATUS_LABELS[order.status] || order.status}
              </p>
              {financing && (
                <p className="text-xs text-accent-700 mt-0.5 flex items-center gap-1">
                  <i className="ri-hand-coin-line"></i>Aprobación pendiente de Sistecrédito
                </p>
              )}
              {order.financingRejected && (
                <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                  <i className="ri-close-circle-line"></i>Financiamiento rechazado por Sistecrédito
                </p>
              )}
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Canal</p>
              <p className="text-sm font-medium text-foreground-900 capitalize">
                {order.channel === 'ecommerce' ? 'Tienda online' : order.channel === 'direct' ? 'Venta directa' : order.channel || '—'}
              </p>
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Cliente</p>
              <p className="text-sm font-medium text-foreground-900">{order.customerName || '—'}</p>
              {order.customerCity && (
                <p className="text-xs text-foreground-500 mt-0.5">{order.customerCity}</p>
              )}
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Contacto</p>
              <p className="text-sm font-medium text-foreground-900">{order.customerPhone || '—'}</p>
              {order.customerEmail && (
                <p className="text-xs text-foreground-500 mt-0.5 truncate">{order.customerEmail}</p>
              )}
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Medio de pago</p>
              <p className="text-sm font-medium text-foreground-900">
                {order.paymentMethod === 'cash'
                  ? 'Contra entrega'
                  : METHOD_LABELS[order.paymentMethod] || order.paymentMethod || '—'}
              </p>
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Fecha</p>
              <p className="text-sm font-medium text-foreground-900">
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleDateString('es-CO', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '—'}
              </p>
            </div>
          </div>

          {/* Financiamiento pendiente: regenerar el enlace para retomar la solicitud */}
          {financing && (
            <div className="border border-accent-200 bg-accent-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-accent-800">
                <i className="ri-hand-coin-line"></i>
                <span className="font-medium">Financiamiento pendiente de aprobación</span>
              </div>
              <p className="text-xs text-accent-700 mt-1 leading-relaxed">
                Si el cliente perdió el enlace, puedes regenerarlo para el mismo pedido (sin crear uno nuevo ni duplicar el stock).
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <button
                  onClick={handleReopen}
                  disabled={reopenLoading}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-accent-300 text-accent-700 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {reopenLoading ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Generando...</>
                  ) : (
                    <><i className="ri-refresh-line"></i>Regenerar enlace</>
                  )}
                </button>
              </div>
              {reopenError && <p className="text-xs text-red-600 mt-2">{reopenError}</p>}
              {reopenUrl && (
                <div className="mt-2.5 flex flex-col sm:flex-row gap-2">
                  <input
                    readOnly
                    value={reopenUrl}
                    className="flex-1 min-w-0 text-xs border border-background-200 rounded-lg px-2.5 py-2 bg-white text-foreground-600"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
                  >
                    <i className={reopenCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                    {reopenCopied ? 'Copiado' : 'Copiar'}
                  </button>
                  <a
                    href={reopenUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 border border-accent-300 text-accent-700 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap font-medium"
                  >
                    <i className="ri-external-link-line"></i>Abrir
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Resumen del intento de Sistecrédito: cuotas, ID de crédito y
              motivo del rechazo. Se muestra aunque el pedido se haya
              reintentado después con otro método de pago. */}
          {(order.sistecreditoInstallments !== null ||
            order.sistecreditoCreditId ||
            order.financingRejected ||
            order.sistecreditoAttemptStatus) && (
              <div
                className={`border rounded-lg p-3 ${
                  scrRejected ? 'border-red-200 bg-red-50' : 'border-secondary-200 bg-secondary-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
                    <i className="ri-hand-coin-line text-secondary-600"></i>
                    Intento de financiamiento Sistecrédito
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${scrAttemptBadge.className}`}
                  >
                    <i className={scrAttemptBadge.icon}></i>
                    {scrAttemptBadge.label}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mt-2.5 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-foreground-500">Cuotas</span>
                    <span className="font-medium text-foreground-900">
                      {order.sistecreditoInstallments !== null ? order.sistecreditoInstallments : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-foreground-500">ID de crédito</span>
                    <span className="font-medium text-foreground-900 break-all text-right">
                      {order.sistecreditoCreditId || '—'}
                    </span>
                  </div>
                </div>
                {scrRejected && (
                  <div className="mt-2.5 pt-2.5 border-t border-red-200">
                    <p className="text-xs text-foreground-500 mb-0.5">Motivo del rechazo</p>
                    <p className="text-sm text-red-700">
                      {order.sistecreditoRejectionReason || 'Sistecrédito no registró un motivo específico.'}
                    </p>
                  </div>
                )}
              </div>
            )}

          {/* Historial COMPLETO de intentos de Sistecrédito (no solo el último). */}
          {order.sistecreditoAttempts.length > 0 && (
            <div className="border border-background-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
                  <i className="ri-history-line text-secondary-600"></i>
                  Historial de intentos de Sistecrédito
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-background-100 text-foreground-600">
                  {order.sistecreditoAttempts.length}{' '}
                  {order.sistecreditoAttempts.length === 1 ? 'intento' : 'intentos'}
                </span>
              </div>
              <ol className="mt-3 space-y-2.5">
                {order.sistecreditoAttempts.map((attempt, index) => {
                  const badge = attemptStatusBadge(attempt.status);
                  return (
                    <li key={attempt.id || index} className="flex items-start gap-2.5">
                      <span
                        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${badge.className}`}
                      >
                        <i className={`${badge.icon} text-sm`}></i>
                      </span>
                      <div className="flex-1 min-w-0 border border-background-100 rounded-lg p-2.5 bg-background-50">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${badge.className}`}
                          >
                            <i className={badge.icon}></i>
                            {badge.label}
                          </span>
                          <span className="text-[11px] text-foreground-500 whitespace-nowrap">
                            {attempt.createdAt
                              ? new Date(attempt.createdAt).toLocaleString('es-CO', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '—'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                          <div className="flex justify-between gap-3">
                            <span className="text-foreground-500">Cuotas</span>
                            <span className="font-medium text-foreground-900">
                              {attempt.installments !== null ? attempt.installments : '—'}
                            </span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-foreground-500">ID de crédito</span>
                            <span className="font-medium text-foreground-900 break-all text-right">
                              {attempt.creditId || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-foreground-500">Monto</span>
                            <span className="font-medium text-foreground-900">
                              ${attempt.amount.toLocaleString()}
                            </span>
                          </div>
                          {attempt.transactionRef && (
                            <div className="flex justify-between gap-3">
                              <span className="text-foreground-500">Referencia</span>
                              <span className="font-medium text-foreground-900 break-all text-right">
                                {attempt.transactionRef}
                              </span>
                            </div>
                          )}
                        </div>
                        {attempt.rejectionReason && (
                          <div className="mt-2 pt-2 border-t border-background-100">
                            <p className="text-[11px] text-foreground-500 mb-0.5">Motivo del rechazo</p>
                            <p className="text-xs text-red-700">{attempt.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Productos */}
          <div>
            <p className="text-sm font-semibold text-foreground-900 mb-2">Productos</p>
            {order.items.length === 0 ? (
              <p className="text-sm text-foreground-500">Sin líneas registradas.</p>
            ) : (
              <div className="border border-background-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[420px]">
                  <thead>
                    <tr className="border-b border-background-100 bg-background-50 text-left">
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500">Producto</th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500">Talla</th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500 text-right">Cant.</th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500 text-right">P. Unit.</th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((it) => (
                      <tr key={it.id} className="border-b border-background-100 last:border-0">
                        <td className="px-3 py-2 text-foreground-900">{it.productName || '—'}</td>
                        <td className="px-3 py-2 text-foreground-600">{it.sizeLabel || '—'}</td>
                        <td className="px-3 py-2 text-right text-foreground-700">{it.quantity}</td>
                        <td className="px-3 py-2 text-right text-foreground-700">
                          ${it.unitPrice.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground-900 font-medium">
                          ${(it.quantity * it.unitPrice).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Datos de envío */}
          {order.hasShipping && (
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-1.5">
                <i className="ri-truck-line text-accent-600"></i>Datos de envío (Interrapidísimo)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="flex justify-between gap-3 sm:col-span-2">
                  <span className="text-foreground-500">Destinatario</span>
                  <span className="font-medium text-foreground-900 text-right">
                    {order.recipientName || order.customerName || '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-foreground-500">Celular</span>
                  <span className="font-medium text-foreground-900">
                    {order.recipientPhone || order.customerPhone || '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-foreground-500">Cédula / NIT</span>
                  <span className="font-medium text-foreground-900">{order.recipientIdNumber || '—'}</span>
                </div>
                <div className="flex justify-between gap-3 sm:col-span-2">
                  <span className="text-foreground-500">Ciudad / Departamento</span>
                  <span className="font-medium text-foreground-900 text-right">
                    {[order.shippingCity || order.customerCity, order.shippingDepartment]
                      .filter(Boolean)
                      .join(' — ') || '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-3 sm:col-span-2">
                  <span className="text-foreground-500">Dirección</span>
                  <span className="font-medium text-foreground-900 text-right">
                    {order.shippingAddress || '—'}
                  </span>
                </div>
                {order.shippingCost > 0 && (
                  <div className="flex justify-between gap-3 sm:col-span-2 pt-2 border-t border-background-100">
                    <span className="text-foreground-500">Flete cobrado</span>
                    <span className="font-medium text-foreground-900">
                      ${order.shippingCost.toLocaleString()}
                    </span>
                  </div>
                )}
                {order.estimatedShippingCost > 0 && (
                  <div className="flex justify-between gap-3 sm:col-span-2 pt-2 border-t border-background-100">
                    <span className="text-foreground-500 flex items-center gap-1.5">
                      Flete estimado (interno)
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        No se cobró
                      </span>
                    </span>
                    <span className="font-medium text-foreground-900">
                      ${order.estimatedShippingCost.toLocaleString()}
                    </span>
                  </div>
                )}
                {order.estimatedCodCommission > 0 && (
                  <div className="flex justify-between gap-3 sm:col-span-2 pt-2 border-t border-background-100">
                    <span className="text-foreground-500 flex items-center gap-1.5">
                      Comisión contraentrega estimada (interno)
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        No se cobró
                      </span>
                    </span>
                    <span className="font-medium text-foreground-900">
                      ${order.estimatedCodCommission.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notas */}
          {order.notes && (
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500 mb-1">Notas del pedido</p>
              <p className="text-sm text-foreground-800">{order.notes}</p>
            </div>
          )}

          {/* Totales */}
          <div className="border-t border-background-200 pt-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-foreground-500">Productos</span>
              <span className="text-foreground-700">${itemsTotal.toLocaleString()}</span>
            </div>
            {shippingCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-500">Envío</span>
                <span className="text-foreground-700">${shippingCost.toLocaleString()}</span>
              </div>
            )}
            {order.codSurcharge > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-500">Recargo contraentrega</span>
                <span className="text-foreground-700">${order.codSurcharge.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-semibold">
              <span className="text-foreground-900">Total</span>
              <span className="text-foreground-950">${total.toLocaleString()}</span>
            </div>
          </div>

          {/* Acciones */}
          <div className="border-t border-background-200 pt-3 flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => {
                setAutoPrintReceipt(false);
                setShowReceipt(true);
              }}
              className="flex-1 text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-2-line mr-1.5"></i>Descargar comprobante
            </button>
            <button
              onClick={() => {
                setAutoPrintReceipt(true);
                setShowReceipt(true);
              }}
              className="flex-1 text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-printer-line mr-1.5"></i>Imprimir
            </button>
            <button
              onClick={handleShippingLabel}
              className="flex-1 text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-file-paper-2-line mr-1.5"></i>Guía de Envío
            </button>
          </div>

          {order.saleNumber && (
            <div className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
              <i className="ri-checkbox-circle-line text-emerald-600"></i>
              <span className="text-emerald-800">
                Venta asociada: <strong>{order.saleNumber}</strong>
              </span>
              <Link
                to="/admin/ventas"
                className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 font-medium whitespace-nowrap"
              >
                Ver ventas
              </Link>
            </div>
          )}
        </div>
      </Modal>

      {showReceipt && (
        <ReceiptModal
          receipt={{
            title: 'Comprobante de Pedido',
            number: order.orderNumber,
            date: order.createdAt,
            customerName: order.customerName,
            customerPhone: order.customerPhone,
            customerCity: order.shippingCity || order.customerCity,
            customerAddress: order.shippingAddress,
            paymentMethod: order.paymentMethod,
            paymentStatus: order.paymentStatus,
            items: order.items.map((it) => ({
              productName: it.productName,
              sizeLabel: it.sizeLabel,
              quantity: it.quantity,
              unitPrice: it.unitPrice,
              subtotal: it.quantity * it.unitPrice,
            })),
            total: order.total,
            notes: order.notes,
          }}
          onClose={() => {
            setShowReceipt(false);
            setAutoPrintReceipt(false);
          }}
          autoPrint={autoPrintReceipt}
        />
      )}
    </>
  );
}